/** @format */

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import {Check} from 'lucide-react';

import {cn, addTWPrefixToClasses} from '@/lib/utils';
import {cva} from 'class-variance-authority'; // Assuming you have class-variance-authority installed

const checkboxVariants = cva(
    'tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-fs-4 tw-fw-600 tw-transition-colors tw-disabled:pointer-events-none tw-disabled:muted tw-rounded-[9999px]',
    {
        variants: {
            variant: {
                primary:
                    'tw-border-2 tw-border-2 tw-border-primary data-[state=checked]:tw-bg-primary',
                secondary:
                    'tw-border-2 tw-border-gray-500 hover:tw-bg-gray-800 data-[state=checked]:tw-bg-gray-400',
                danger: 'tw-border-2 tw-border-danger hover:tw-bg-danger data-[state=checked]:tw-bg-danger',
                warning:
                    'tw-border-2 tw-border-warning hover:tw-bg-warning data-[state=checked]:tw-bg-warning',
                success:
                    'tw-border-2 tw-border-success hover:tw-bg-success data-[state=checked]:tw-bg-success',
                info: 'tw-border-2 tw-border-indigo-300 hover:tw-bg-indigo-200 data-[state=checked]:tw-bg-indigo-200',
                disabled: 'tw-bg-gray-200 tw-text-gray-500 tw-pointer-events-none'
            },
            size: {
                sm: 'tw-py-3 tw-px-3',
                md: 'tw-py-4 tw-px-4',
                lg: 'tw-py-5 tw-px-5'
            }
        },
        defaultVariants: {
            variant: 'primary',
            size: 'md'
        }
    }
);

const Checkbox = React.forwardRef(({className, variant, size = 'md', ...props}, ref) => {
    const svgSize = {
        sm: 'tw-h-3 tw-w-3',
        md: 'tw-h-4 tw-w-4',
        lg: 'tw-h-6 tw-w-6'
    };
    return (
        <CheckboxPrimitive.Root
            ref={ref}
            className={cn(
                checkboxVariants({variant, size}),
                `tw-peer tw-h-4 tw-w-4 tw-shrink-0 tw-rounded-sm tw-ring-offset-background focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ring focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 data-[state=checked]:tw-text-white`,
                addTWPrefixToClasses(className)
            )}
            {...props}>
            <CheckboxPrimitive.Indicator
                className={cn('tw-flex tw-items-center tw-justify-center tw-text-white')}>
                <Check className={cn('tw-shrink-0 tw-text-white', svgSize[size])} />
            </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
    );
});

Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export {Checkbox};
